import { SearchInputExpanded } from 'module/ui/components/atoms/input/search-input-expanded';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from 'antd';
import moment from 'moment';
import { useGetAllMeetingsQueryWithScrollPagination } from 'queries/meeting';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 400px;
  border-radius: 8px;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  height: 32px;
  margin: 16px 16px 8px;
  background: var(--white-100);
  border-radius: 8px;
`;

const SearchInput = styled(SearchInputExpanded)`
  width: 100%;
  height: 100%;
  border: none;
  font-family: var(--font-family);
  font-size: 14px;
  line-height: 1.286;
`;

const MeetingList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: var(--spacing-03) var(--spacing-05);
  gap: var(--spacing-03);
  overflow: scroll;
`;

const MeetingItem = styled.div<{ $isSelected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: var(--spacing-03);
  background: ${(props) =>
    props.$isSelected ? '#D0E2FF' : 'var(--white-100)'};
  border-bottom: 1px solid var(--divider-default);
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.$isSelected ? '#D0E2FF' : 'var(--scarlet-gray-10)'};
  }
`;

const CheckIcon = styled(FontAwesomeIcon)<{ $visible: boolean }>`
  width: 16px;
  height: 16px;
  color: var(--text-primary);
  opacity: ${(props) => (props.$visible ? 1 : 0)};
`;

const MeetingContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--spacing-03);
  flex: 1;
  min-width: 0;
  padding-right: var(--spacing-03);
`;

const MeetingName = styled.div`
  font-family: var(--font-family);
  font-size: 14px;
  line-height: 1.286;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MeetingDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-family);
  font-size: 12px;
  line-height: 1.333;
`;

const MeetingTime = styled.span`
  color: var(--text-secondary);
  white-space: nowrap;
`;

const Dot = styled.span`
  width: 4px;
  height: 4px;
  background-color: var(--text-secondary);
  border-radius: 50%;
  flex-shrink: 0;
`;

const Participants = styled.span`
  color: var(--text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 52px;
  color: var(--text-tertiary);
`;

const LoadMoreState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-03);
  color: var(--text-tertiary);
  font-size: 12px;
`;

interface MeetingListProps {
  onMeetingSelect?: (meetingId: number, meetingName: string) => void;
  selectedMeetingId?: number;
}

export const MeetingListComponent: React.FC<MeetingListProps> = ({
  onMeetingSelect,
  selectedMeetingId,
}) => {
  const {
    meetings,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetAllMeetingsQueryWithScrollPagination(undefined, {
    enabled: true,
    limit: 25,
  });
  const [searchQuery, setSearchQuery] = React.useState('');

  const filteredMeetings = useMemo(() => {
    return meetings
      .filter((meeting) => meeting.meetingId !== -1)
      .filter((meeting) =>
        meeting.meetingName.toLowerCase().includes(searchQuery.toLowerCase()),
      );
  }, [meetings, searchQuery]);

  const handleListScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const thresholdPx = 80;

      if (
        hasNextPage &&
        !isFetchingNextPage &&
        el.scrollHeight - el.scrollTop - el.clientHeight < thresholdPx
      ) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  const formatParticipants = (participants: any[]) => {
    if (!participants?.length) {
      return 'No participants';
    }

    const firstTwo = participants
      .slice(0, 2)
      .map((p) => p.firstName || p.emailId)
      .join(', ');

    if (participants.length <= 2) {
      return firstTwo;
    }

    return `${firstTwo} and ${participants.length - 2} ${participants.length - 2 === 1 ? 'other' : 'others'}`;
  };

  if (isLoading && meetings.length === 0) {
    return <LoadingState>Loading meetings...</LoadingState>;
  }

  return (
    <Container>
      <SearchContainer>
        <SearchInput
          placeholder="Search meetings"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery('')}
        />
      </SearchContainer>

      <MeetingList onScroll={handleListScroll}>
        {filteredMeetings.map((meeting) => (
          <MeetingItem
            key={meeting.meetingId}
            $isSelected={selectedMeetingId === meeting.meetingId}
            onClick={() =>
              onMeetingSelect?.(meeting.meetingId, meeting.meetingName)
            }
          >
            <MeetingContent>
              <MeetingName>{meeting.meetingName}</MeetingName>
              <MeetingDetails>
                <MeetingTime>
                  {moment(meeting.startTime).format('dddd D MMM h:mmA')}
                </MeetingTime>
                <Dot />
                <Tooltip
                  title={meeting.meta.participants
                    ?.slice(2)
                    .map((p) => p.firstName || p.emailId)
                    .join(', ')}
                >
                  <Participants>
                    {formatParticipants(meeting.meta.participants)}
                  </Participants>
                </Tooltip>
              </MeetingDetails>
            </MeetingContent>
            <CheckIcon
              icon={faCheck}
              $visible={selectedMeetingId === meeting.meetingId}
            />
          </MeetingItem>
        ))}

        {filteredMeetings.length === 0 && (
          <LoadingState>No meetings found</LoadingState>
        )}
        {hasNextPage && isFetchingNextPage && (
          <LoadMoreState>Loading more meetings...</LoadMoreState>
        )}
      </MeetingList>
    </Container>
  );
};
